import { useLazyQuery } from '@apollo/client';
import {
  Center,
  Flex,
  HStack,
  Text,
  useDisclosure,
  VStack,
} from '@chakra-ui/react';
import { AccordionPanel } from '@gamma/ui/layout';
import { GraphQLReqStatus } from '@gamma/ui/progress';
import {
  QUERY_ALERTS_YARA_SUMMARY,
  YaraAlert,
} from '@investigator/features/detections/queries';
import { buildAlertIdQuery } from '../../utils/buildAlertIdQuery';
import moment from 'moment';
import { TimestampCell } from '@investigator/shared/components';
import { useContext, useEffect, useRef } from 'react';
import { useHumioToken } from '@investigator/shared/providers';
import {
  AlertSessionSummary,
  QueryAlertsYaraSummaryQuery,
} from '@investigator/shared/generated/graphql';
import { i18n } from '@investigator/shared/localization';
import {
  DetailsGrid,
  DetailsGridRow,
} from '../../../../../../../../../../components';
import { SanitizedYaraAlert } from '../../../DetectionAlerts';
import { InvestigateLogsTableModal } from '../InvestigateLogsTableModal';
import { LogscaleDetails } from '../LogscaleDetails';
import { buildSearchQueryString } from '../LogscaleDetails/utils/buildSearchQueryString';
import { LogscaleMenuButton } from '../LogscaleMenuButton';
import { SessionSummary } from '../SessionSummary';
import { useDetectionContext } from '@investigator/features/detections/context';
import { AppConfigurationsContext } from '@investigator/shared/providers';
import type { SessionSummaryData } from '@investigator/features/detections/queries/types';
const { alerts, detection } = i18n.pages.detections;

interface YaraButtonsColProps {
  item: SanitizedYaraAlert;
  logScaleQuery?: string;
  showContent?: boolean;
}

export const YaraButtonsCol = ({
  item,
  logScaleQuery,
  showContent,
}: YaraButtonsColProps) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  // Fix: Ensure observed is a number
  const observed = item.alert_timestamp?.observed ?? 0;
  const formattedTime = moment.unix(observed).format();
  const start = moment(formattedTime).subtract(1, 'hours').unix();
  const end = moment(formattedTime).add(24, 'hours').unix();

  const searchQueryString = buildSearchQueryString({
    item: {
      ...item,
      alert_name: item.alert_info?.alert_name ?? '',
      alert_info: {
        alert_type: item.alert_info?.alert_type ?? '',
        alert_name: item.alert_info?.alert_name ?? '',
      },
      severity: (item as YaraAlertWithSeverity).severity ?? 0,
      is_custom_severity:
        (item as YaraAlertWithSeverity).is_custom_severity ?? false,
      // Fix: Ensure alert_timestamp is always { observed: number }
      alert_timestamp: { observed: item.alert_timestamp?.observed ?? 0 },
      alert_entity: {
        entity_name: item.alert_entity?.entity_name ?? '',
        entity_type: item.alert_entity?.entity_type ?? '',
      },
      first_seen: 0,
      last_seen: 0,
    },
    logScaleQuery,
    start,
    end,
  });

  return (
    <Flex flexGrow={1}>
      <LogscaleMenuButton
        searchQueryString={searchQueryString}
        investigateModalOnOpen={onOpen}
        showContent={showContent}
      />
      <InvestigateLogsTableModal
        isOpen={isOpen}
        onClose={onClose}
        onOpen={onOpen}
        alertsName={item.alert_info?.alert_name ?? ''}
        alertsUid={item.uid}
        alertsTimestamp={formattedTime}
        alertsTenant={item.tenant}
        logScaleQuery={logScaleQuery}
      />
    </Flex>
  );
};

interface AlertRowYaraProps {
  item: SanitizedYaraAlert;
  logScaleQuery?: string;
  showContent?: boolean;
}

export const AlertRowYara = ({
  item,
  logScaleQuery,
  showContent,
}: AlertRowYaraProps) => {
  return (
    <HStack spacing={4}>
      <VStack alignItems="start">
        <TimestampCell
          timestamp={item.alert_timestamp?.observed ?? 0}
          isFromNow={false}
        />
        <HStack color="text.200">
          <Text textStyle="body-md">{item.alert_info?.alert_name ?? ''}</Text>
          <Text textStyle="body-md">|</Text>
          <Text textStyle="body-md">
            {item.alert_entity?.entity_name ?? ''}
          </Text>
        </HStack>
      </VStack>
      <YaraButtonsCol
        item={item}
        logScaleQuery={logScaleQuery}
        showContent={showContent}
      />
    </HStack>
  );
};

interface AlertRowSplitYaraProps {
  item: SanitizedYaraAlert;
  loading: boolean | undefined;
  isGPTPrivateDataEnabled: boolean;
  logScaleQuery?: string;
}

export const AlertRowSplitYara = ({
  item,
  loading,
  isGPTPrivateDataEnabled,
  logScaleQuery,
}: AlertRowSplitYaraProps) => {
  const { appConfigurations } = useContext(AppConfigurationsContext);
  const { token: sessionToken } = useHumioToken();
  const { detectionData } = useDetectionContext();
  const isLLMSummary = appConfigurations?.feature_flags.llm_summary || false;
  const summary = useRef<AlertSessionSummary | null | undefined>(undefined);
  // Fix: Ensure observed is a number
  const observed = item.alert_timestamp?.observed ?? 0;
  const time = moment.unix(observed);
  const formattedTime = time.format();

  const noFilterVariables = {
    query: buildAlertIdQuery(item.alert_id),
    log_search_client_session_id: sessionToken || '',
  };

  const [
    fetchSummaryData,
    { data: sessionSummaryData, loading: sessionSummaryLoading },
  ] = useLazyQuery<QueryAlertsYaraSummaryQuery>(QUERY_ALERTS_YARA_SUMMARY, {
    variables: noFilterVariables,
    onError: (error) => {
      console.error(error);
    },
  });

  useEffect(() => {
    if (
      detectionData?.tenant_info?.tenant_id &&
      detectionData?.alert_entity?.entity_id &&
      isLLMSummary &&
      isGPTPrivateDataEnabled
    ) {
      fetchSummaryData();
    }
  }, [detectionData, fetchSummaryData, isGPTPrivateDataEnabled, isLLMSummary]);

  const yaraAlertData = sessionSummaryData?.queryAlertsPaginated
    ?.alerts?.[0] as YaraAlert | undefined;
  // Fix: Type assertion for session_summary
  const sessionSummary = (yaraAlertData as YaraAlertWithSessionSummary)
    ?.session_summary;

  useEffect(() => {
    const alert = sessionSummaryData?.queryAlertsPaginated?.alerts?.[0];
    // This check is here to satisfy typescript without casting.
    // The union type considers all alerts because we use a
    // paginated query
    if (alert?.__typename === 'YaraAlert') {
      summary.current = alert?.session_summary;
    } else if (alert) {
      console.error(`Wanted Yara Alert but got: ${alert?.__typename}`);
    }
  }, [sessionSummaryData]);

  if (loading) {
    return (
      <Center w="100%">
        <GraphQLReqStatus loading={loading} />
      </Center>
    );
  }

  // Map AlertSessionSummary to SessionSummaryData if present
  const mappedSessionSummary: SessionSummaryData | undefined = sessionSummary
    ? {
        ioc: Array.isArray(sessionSummary.ioc)
          ? (sessionSummary.ioc.filter(Boolean) as string[])
          : [],
        alert_summary: Array.isArray(sessionSummary.alert_summary)
          ? (sessionSummary.alert_summary.filter(Boolean) as string[])
          : [],
        alert_beacons: Array.isArray(sessionSummary.alert_beacons)
          ? (sessionSummary.alert_beacons.filter(Boolean) as string[])
          : [],
        unusual_findings: Array.isArray(sessionSummary.unusual_findings)
          ? (sessionSummary.unusual_findings.filter(Boolean) as string[])
          : [],
        attack_tactics: Array.isArray(sessionSummary.attack_tactics)
          ? (sessionSummary.attack_tactics.filter(Boolean) as string[])
          : [],
      }
    : undefined;

  return (
    <VStack mb={2} w="100%" alignItems="start">
      <LogscaleDetails
        alertsUid={item?.uid}
        alertsName={item?.alert_info?.alert_name ?? ''}
        alertsTimestamp={formattedTime}
        alertsTenant={item?.tenant}
        logScaleQuery={logScaleQuery}
        alertType="yara_corelight"
        childrenBelowHeader={
          <AccordionPanel
            title={detection.alertDetails}
            data-testid="yara-alerts-wrapper"
            reduceMotion
          >
            <DetailsGrid>
              <DetailsGridRow title={alerts.name}>
                <Text overflowWrap="anywhere">
                  {item?.alert_info?.alert_name ?? ''}
                </Text>
              </DetailsGridRow>
              <DetailsGridRow title={alerts.timestamp}>
                <Text>{formattedTime}</Text>
              </DetailsGridRow>
              <DetailsGridRow title={alerts.uid}>
                <Text>{item?.uid ?? ''}</Text>
              </DetailsGridRow>
              <DetailsGridRow title={alerts.sourceIp}>
                <VStack alignItems="start" spacing={1}>
                  {item?.source_entities?.map((sourceIp, idx) => (
                    <Text key={idx}>{sourceIp?.entity_name ?? ''}</Text>
                  ))}
                </VStack>
              </DetailsGridRow>
              <DetailsGridRow title="Destination IP">
                <VStack alignItems="start" spacing={1}>
                  {item?.destination_entities?.map((destIp, idx) => (
                    <Text key={idx}>{destIp?.entity_name ?? ''}</Text>
                  ))}
                </VStack>
              </DetailsGridRow>
              <DetailsGridRow title="Alert Type">
                <Text>{item?.alert_info?.alert_type ?? ''}</Text>
              </DetailsGridRow>
              {item?.match_meta && item?.match_meta.length > 0 && (
                <DetailsGridRow title="Rule Details">
                  <Text>{item.match_meta.join(' | ')}</Text>
                </DetailsGridRow>
              )}
            </DetailsGrid>
            {isLLMSummary && isGPTPrivateDataEnabled && (
              <SessionSummary
                sessionSummaryData={mappedSessionSummary}
                loading={sessionSummaryLoading}
              />
            )}
          </AccordionPanel>
        }
      />
    </VStack>
  );
};

// Local type for YaraAlert with severity and is_custom_severity
interface YaraAlertWithSeverity extends SanitizedYaraAlert {
  severity?: number;
  is_custom_severity?: boolean;
}
// Local type for session_summary
interface YaraAlertWithSessionSummary extends YaraAlert {
  session_summary?: AlertSessionSummary;
}
